'use client';

import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useState, useEffect } from 'react';
import ThemeToggle from '@/components/ui/ToggleComp';
import { useAuth } from '@/lib/auth/authContext';
import {
  LayoutGrid,
  ListChecks,
  LogIn,
  LogOut,
  Menu,
  MessageCircle,
  Sparkles,
  X,
} from 'lucide-react';

// Routes that belong to the tools section, used to highlight the "Tools" link
const TOOL_ROUTE_PREFIXES = [
  '/tools',
  '/filtered-tick-analysis',
  '/tick-analysis',
  '/volume-analyzer',
  '/volume-scanner',
  '/esm-stock-analysis',
  '/esm-2-to-1-stocks',
  '/esm-2to1-prediction',
  '/esm-2-overdue-stocks',
  '/esm-exited-stocks',
  '/asm-stock-history',
  '/gsm-stock-analysis',
  '/deals',
  '/stocks',
  '/big-buy-at-low',
  '/broker-platform',
  '/data-update',
];

export default function Navbar() {
  const pathname = usePathname();
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const [hasMounted, setHasMounted] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // Reset image error when user changes
  useEffect(() => {
    setImageError(false);
  }, [user?.profile_picture]);

  const isActive = (path: string) => {
    if (!pathname) return false;
    if (path === '/tools') {
      return TOOL_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
    }
    return pathname === path || pathname.startsWith(`${path}/`);
  };

  const navItems = [
    { href: '/tools', label: 'Tools', icon: <LayoutGrid className="h-4 w-4" /> },
    { href: '/ai-scans', label: 'AI Scans', icon: <Sparkles className="h-4 w-4" /> },
    ...(hasMounted && isAuthenticated
      ? [{ href: '/watchlist', label: 'Watchlist', icon: <ListChecks className="h-4 w-4" /> }]
      : []),
    { href: '/contact', label: 'Contact', icon: <MessageCircle className="h-4 w-4" /> },
  ];

  const showLogin = !hasMounted || isLoading || !isAuthenticated;

  const userLabel =
    user?.first_name || user?.email?.split('@')[0] || user?.phone_number || 'User';

  const avatar = user && (
    user.profile_picture && !imageError ? (
      <Image
        src={user.profile_picture}
        alt={userLabel}
        width={32}
        height={32}
        className="h-8 w-8 rounded-full object-cover ring-2 ring-emerald-500/60"
        onError={() => setImageError(true)}
        unoptimized
      />
    ) : (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/15 text-sm font-semibold text-emerald-600 dark:text-emerald-400 ring-2 ring-emerald-500/40">
        {(user.first_name?.[0] || user.email?.[0] || 'U').toUpperCase()}
      </div>
    )
  );

  return (
    <header className="sticky top-0 z-40 border-b border-zinc-200/70 dark:border-white/[0.08] bg-zinc-50/85 dark:bg-zinc-950/80 backdrop-blur-md">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Brand */}
          <Link
            href="/"
            className="group flex shrink-0 items-center gap-2.5"
            title="Go to Home"
          >
            <Image
              src="/brand/draamview-icon.png"
              alt=""
              width={36}
              height={36}
              className="h-9 w-9 rounded-xl transition-transform group-hover:scale-105"
              priority
            />
            <span className="font-display text-2xl leading-none text-zinc-900 dark:text-white">
              Draam<span className="italic text-emerald-700 dark:text-emerald-400">view</span>
            </span>
          </Link>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center gap-2">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`relative flex items-center gap-2 px-3 py-1.5 text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? 'text-zinc-900 dark:text-white after:absolute after:inset-x-3 after:-bottom-[13px] after:h-[2px] after:bg-emerald-600 dark:after:bg-emerald-400'
                    : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'
                }`}
              >
                {item.icon}
                {item.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <ThemeToggle />

            {showLogin ? (
              <Link
                href="/login"
                className="hidden md:inline-flex items-center gap-2 rounded-md bg-zinc-900 dark:bg-zinc-50 px-4 py-2 text-sm font-semibold text-zinc-50 dark:text-zinc-900 transition-colors hover:bg-emerald-700 dark:hover:bg-emerald-300"
              >
                <LogIn className="h-4 w-4" />
                Sign in
              </Link>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <div className="flex items-center gap-2.5 rounded-full border border-zinc-200/80 dark:border-white/[0.08] bg-zinc-50/80 dark:bg-white/[0.04] py-1 pl-1 pr-3">
                  {avatar}
                  <span className="max-w-[10rem] truncate text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    {userLabel}
                  </span>
                </div>
                <button
                  onClick={logout}
                  title="Sign out"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full text-zinc-500 hover:text-red-600 hover:bg-red-50 dark:text-zinc-400 dark:hover:text-red-400 dark:hover:bg-red-500/10 transition-colors"
                >
                  <LogOut className="h-[18px] w-[18px]" />
                </button>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen((open) => !open)}
              className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-zinc-200/70 dark:border-white/[0.08] py-3 animate-fadeIn">
            <div className="flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 text-base font-medium transition-colors ${
                    isActive(item.href)
                      ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                      : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-white/[0.06]'
                  }`}
                >
                  {item.icon}
                  {item.label}
                </Link>
              ))}

              <div className="mt-2 border-t border-zinc-200/70 dark:border-white/[0.08] pt-3">
                {showLogin ? (
                  <Link
                    href="/login"
                    className="flex items-center justify-center gap-2 rounded-xl bg-zinc-900 dark:bg-white px-4 py-3 text-base font-semibold text-white dark:text-zinc-900"
                  >
                    <LogIn className="h-5 w-5" />
                    Sign in
                  </Link>
                ) : (
                  <div className="flex items-center justify-between gap-3 rounded-xl bg-zinc-50 dark:bg-white/[0.04] px-4 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      {avatar}
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-zinc-800 dark:text-zinc-200">
                          {userLabel}
                        </p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">Signed in</p>
                      </div>
                    </div>
                    <button
                      onClick={logout}
                      className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
